import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = ["Technical Specs", "Ordering Process", "Delivery and Lead Time", "Warranty"];

export default function FAQs() {
  const [faqs, setFaqs] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    base44.entities.FAQItem.list("order", 100).then(setFaqs);
  }, []);

  const filtered = (cat) =>
    (faqs || []).filter(
      (f) =>
        f.category === cat &&
        (search.trim() === "" ||
          f.question.toLowerCase().includes(search.toLowerCase()) ||
          f.answer.toLowerCase().includes(search.toLowerCase()))
    );

  return (
    <>
      <section className="bg-[#0A0A0A] pt-40 pb-28 clip-diagonal-bottom relative overflow-hidden">
        <div className="absolute top-0 right-0 h-full w-40 bg-[#E4001C]/70 hidden md:block" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <p className="font-tech font-semibold uppercase tracking-[0.25em] text-[#E4001C] text-sm mb-4">Support</p>
          <h1 className="font-heading font-extrabold uppercase text-white text-5xl md:text-7xl tracking-wide">
            Frequently Asked Questions
          </h1>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative mb-10">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8ADB4]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions…"
              className="w-full border border-gray-300 focus:border-[#E4001C] outline-none pl-12 pr-4 py-4 text-sm transition-colors"
            />
          </div>

          {!faqs ? (
            <div className="py-20 flex justify-center">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-[#E4001C] rounded-full animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue={CATEGORIES[0]}>
              <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 mb-8 justify-start">
                {CATEGORIES.map((c) => (
                  <TabsTrigger key={c} value={c}
                    className="font-tech font-semibold uppercase tracking-widest text-xs px-4 py-2.5 border border-gray-300 rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white data-[state=active]:border-[#0A0A0A]">
                    {c}
                  </TabsTrigger>
                ))}
              </TabsList>
              {CATEGORIES.map((c) => (
                <TabsContent key={c} value={c}>
                  {filtered(c).length === 0 ? (
                    <p className="text-[#A8ADB4] font-tech uppercase tracking-widest text-sm py-8">No questions match your search.</p>
                  ) : (
                    <Accordion type="single" collapsible className="space-y-3">
                      {filtered(c).map((f) => (
                        <AccordionItem key={f.id} value={f.id} className="border border-gray-200 px-6 data-[state=open]:border-[#E4001C]/50">
                          <AccordionTrigger className="font-tech font-semibold text-left text-base hover:text-[#E4001C] hover:no-underline">
                            {f.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-[#171717]/70 leading-relaxed text-sm">
                            {f.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>
    </>
  );
}